import { StatusVariant } from '@factorialco/f0-react'
import {
  BankingValueObjectsPaymentRequestItemStatusEnum,
  SalaryAdvanceValueObjectsRequestStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export type StatusViewerType = 'employee' | 'hr_admin'

export type StatusConfig = {
  label: string
  variant: StatusVariant
  showWarningIcon: boolean
}

const employeeStatusConfig = (
  requestStatus: SalaryAdvanceValueObjectsRequestStatusEnum
): StatusConfig => {
  switch (requestStatus) {
    case 'requested':
      return {
        label: i18n.t('salary_advance.request.status.requested'),
        variant: 'neutral',
        showWarningIcon: false,
      }
    case 'processing':
      return {
        label: i18n.t('salary_advance.request.status.processing'),
        variant: 'info',
        showWarningIcon: false,
      }
    case 'completed':
      return {
        label: i18n.t('salary_advance.request.status.completed'),
        variant: 'positive',
        showWarningIcon: false,
      }
    case 'expired':
      return {
        label: i18n.t('salary_advance.request.status.expired'),
        variant: 'critical',
        showWarningIcon: false,
      }
    default: {
      const _exhaustiveCheck: never = requestStatus
      throw new Error(`Unhandled status: ${_exhaustiveCheck}`)
    }
  }
}

const hrStatusConfig = (
  requestStatus: SalaryAdvanceValueObjectsRequestStatusEnum,
  paymentRequestItemStatus?: BankingValueObjectsPaymentRequestItemStatusEnum | null
): StatusConfig => {
  if (paymentRequestItemStatus === 'rejected') {
    return {
      label: i18n.t('salary_advance.request.status.action_required'),
      variant: 'warning',
      showWarningIcon: true,
    }
  }

  switch (requestStatus) {
    case 'requested':
      return {
        label: i18n.t('salary_advance.request.status.requested_hr_admin'),
        variant: 'warning',
        showWarningIcon: false,
      }
    case 'processing':
      return {
        label: i18n.t('salary_advance.request.status.processing'),
        variant: 'info',
        showWarningIcon: false,
      }
    case 'completed':
      return {
        label: i18n.t('salary_advance.request.status.completed'),
        variant: 'positive',
        showWarningIcon: false,
      }
    case 'expired':
      return {
        label: i18n.t('salary_advance.request.status.expired'),
        variant: 'critical',
        showWarningIcon: false,
      }
    default: {
      const _exhaustiveCheck: never = requestStatus
      throw new Error(`Unhandled status: ${_exhaustiveCheck}`)
    }
  }
}

/**
 * Get status configuration (label and variant) for a salary advance request
 * @param requestStatus - The request status from the backend
 * @param paymentRequestItemStatus - The payment request item status (optional)
 * @param viewerType - Who is viewing: 'employee' or 'hr_admin' (default: 'hr_admin')
 * @returns Object with label (translated), variant, and showWarningIcon for display
 */
export const getStatusConfig = (
  requestStatus: SalaryAdvanceValueObjectsRequestStatusEnum,
  paymentRequestItemStatus?: BankingValueObjectsPaymentRequestItemStatusEnum | null,
  viewerType: StatusViewerType = 'hr_admin'
): StatusConfig => {
  return viewerType === 'employee'
    ? employeeStatusConfig(requestStatus)
    : hrStatusConfig(requestStatus, paymentRequestItemStatus)
}
